/**
 * The Michelin guide, kept beside the programmes without becoming one.
 *
 * Its restaurants live in the catalogue like any venue — same table, same
 * card, same pass on the map — under a programme id of their own, so every
 * screen that lists venues can show them without a second code path. What
 * makes them different is said here once, and imported by the app and the
 * Worker alike:
 *
 * - they are off unless asked for: the map, the list and "Vicino" stay the
 *   map of places seen on television until the "Stellati" chip is pressed, or
 *   a search names one;
 * - they have no episode, no rival and no winner, so nothing that talks about
 *   episodes may count them — zones, twins, "the others of the episode";
 * - their number is stars, not a placing.
 */

export const GUIDE_ID = 'stellati-michelin';
export const GUIDE_YEAR = 2026;
export const GUIDE_NAME = `Stellati Michelin ${GUIDE_YEAR}`;
export const GUIDE_SOURCE =
  'https://en.wikipedia.org/wiki/List_of_Michelin-starred_restaurants_in_Italy';
export const GUIDE_OFFICIAL = 'https://guide.michelin.com/it/it';

/** The same sentence wherever the list is shown: where it comes from, and who we are not. */
export const GUIDE_CREDIT =
  'Dati: Wikipedia, voce “List of Michelin-starred restaurants in Italy”, licenza CC BY-SA 4.0. ' +
  'Visto e Mangiato non è affiliato a Michelin.';

export function isGuide(venue) {
  return venue?.programma === GUIDE_ID;
}

/** Television only: what every count, zone and episode is made of. */
export function onTelevision(venues) {
  return venues.filter((venue) => !isGuide(venue));
}

const STAR_WORDS = { 1: 'una stella', 2: 'due stelle', 3: 'tre stelle' };

/** "due stelle", for sentences. */
export function starsWords(n) {
  return STAR_WORDS[n] ?? `${n} stelle`;
}

/** "★★", for the eye. */
export function starsGlyph(n) {
  return '★'.repeat(Math.max(0, Math.min(3, Number(n) || 0)));
}

/**
 * The address of a town's starred restaurants: `/stellati/milano`. The same
 * slug as a zone's, so a town reads the same in both addresses.
 */
export function guideCitySlug(city) {
  return String(city ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** The guide's restaurants by town, most stars first, then by name. */
export function guideByCity(venues) {
  const byCity = new Map();
  for (const venue of venues) {
    if (!isGuide(venue) || !venue.zona) continue;
    const slug = guideCitySlug(venue.zona);
    if (!byCity.has(slug)) byCity.set(slug, { citta: venue.zona, slug, venues: [] });
    byCity.get(slug).venues.push(venue);
  }
  for (const city of byCity.values()) city.venues.sort(byStars);
  return byCity;
}

export function byStars(a, b) {
  return (
    (b.stelle ?? 0) - (a.stelle ?? 0) ||
    (a.nome ?? a.id).localeCompare(b.nome ?? b.id, 'it')
  );
}

/** "18 ristoranti: 1 con tre stelle, 3 con due, 14 con una". */
export function starsSummary(venues) {
  const n = (stars) => venues.filter((venue) => venue.stelle === stars).length;
  const parts = [];
  if (n(3)) parts.push(`${n(3)} con tre stelle`);
  if (n(2)) parts.push(`${n(2)} con due`);
  if (n(1)) parts.push(`${n(1)} con una`);
  return parts.join(', ');
}

/** Title and description of a town's page, for the Worker and the app's tab. */
export function guideCityHead(city, venues) {
  const n = venues.length;
  const best = venues
    .filter((venue) => venue.stelle >= 2)
    .slice(0, 3)
    .map((venue) => venue.nome);
  return {
    title:
      n === 1
        ? `Ristoranti stellati a ${city}: il ristorante della Guida Michelin ${GUIDE_YEAR}`
        : `Ristoranti stellati a ${city}: i ${n} della Guida Michelin ${GUIDE_YEAR}`,
    description:
      `${n === 1 ? 'Un ristorante' : `${n} ristoranti`} a ${city} nella Guida Michelin ${GUIDE_YEAR}: ` +
      `${starsSummary(venues)}.` +
      (best.length ? ` ${best.join(', ')} e gli altri, sulla mappa.` : ' Sulla mappa, con indirizzo e stato.'),
  };
}

export function guideCityTitle(city) {
  return `Ristoranti stellati a ${city}`;
}

/** Title and description of the guide's own page. */
export function guideHead(venues) {
  const n = venues.length;
  return {
    title: `Ristoranti stellati Michelin ${GUIDE_YEAR}: la mappa dei ${n} in Italia`,
    description:
      `Tutti i ${n} ristoranti italiani con almeno una stella nella Guida Michelin ${GUIDE_YEAR}: ` +
      `${starsSummary(venues)}. Cerca per città, con gli stellati degli chef della TV.`,
  };
}

/** Title and description of one restaurant's page. */
export function guideVenueHead(venue, statusSentence) {
  const where = venue.zona ? `, ${venue.zona}` : '';
  const stars = starsWords(venue.stelle);
  const sentences = [
    `${stars[0].toUpperCase()}${stars.slice(1)} nella Guida Michelin ${GUIDE_YEAR}` +
      (venue.cucina ? `, cucina ${venue.cucina}.` : '.'),
  ];
  if (statusSentence) sentences.push(statusSentence);
  if (venue.indirizzo && venue.indirizzo_confermato) sentences.push(`${venue.indirizzo}.`);
  return {
    title: `${venue.nome}${where}: ${stars} Michelin ${GUIDE_YEAR}`,
    description: sentences.join(' '),
  };
}

/**
 * The chefs people know from television, by the restaurants that carry their
 * name or are known to be theirs. Only certain ones: a wrong chef on a page
 * is worse than none.
 */
const TV_CHEFS = [
  [/cannavacciuolo|villa crespi/i, 'Antonino Cannavacciuolo'],
  [/cracco/i, 'Carlo Cracco'],
  [/francescana|bottura/i, 'Massimo Bottura'],
];

export function tvChef(venue) {
  if (!isGuide(venue)) return null;
  return TV_CHEFS.find(([pattern]) => pattern.test(venue.nome ?? ''))?.[1] ?? null;
}
